import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, X, ChevronLeft, ChevronRight, CheckCircle2, XCircle,
  FileText, ExternalLink, Inbox, RefreshCw, Check, AlertCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { adminPaymentOrdersAPI } from '../../services/api';

// Brand palette (aligned with onboarding primitives — do NOT import primitives here).
const BRAND = {
  greenDark: '#0F6E56',
  green: '#1D9E75',
  text: '#1A1A18',
  muted: '#888780',
  surface: '#FDFCF9',
  surfaceAlt: '#F7F6F1',
  mint: '#E1F5EE',
  border: '#E8E5DD',
  error: '#E24B4A',
};

const TABS = [
  { key: 'pending', label: 'قيد المراجعة' },
  { key: 'approved', label: 'مصادَق' },
  { key: 'rejected', label: 'مرفوض' },
  { key: 'all', label: 'الكل' },
];

const StatusBadge = ({ status }) => {
  const map = {
    pending: { bg: '#FFF4D6', color: '#8A5A00', label: 'قيد المراجعة', dot: '#E8A13C' },
    approved: { bg: BRAND.mint, color: BRAND.greenDark, label: 'مصادَق', dot: BRAND.green },
    rejected: { bg: 'rgba(226,75,74,0.08)', color: BRAND.error, label: 'مرفوض', dot: BRAND.error },
  };
  const m = map[status] || { bg: '#F1F1EF', color: BRAND.muted, label: status || '-', dot: BRAND.muted };
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold"
      style={{ background: m.bg, color: m.color }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: m.dot }} />
      {m.label}
    </span>
  );
};

const SectionTitle = ({ children }) => (
  <div
    className="mt-6 mb-2.5"
    style={{
      fontSize: 11,
      fontWeight: 600,
      color: BRAND.muted,
      letterSpacing: 0.5,
      textTransform: 'uppercase',
    }}
  >
    {children}
  </div>
);

const InfoGrid = ({ data }) => (
  <div
    className="grid grid-cols-1 sm:grid-cols-2 gap-y-2.5 gap-x-5 rounded-xl p-3.5"
    style={{ background: BRAND.surfaceAlt }}
  >
    {data.map(([k, v]) => (
      <div key={k}>
        <div className="text-[11.5px] mb-0.5" style={{ color: BRAND.muted }}>{k}</div>
        <div className="text-[13.5px] font-medium break-words" style={{ color: BRAND.text }}>
          {v || '—'}
        </div>
      </div>
    ))}
  </div>
);

const formatDateTime = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('ar-SA-u-ca-gregory', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return String(value);
  }
};

const formatDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString('ar-SA-u-ca-gregory');
  } catch {
    return String(value);
  }
};

const formatAmount = (n) => {
  const num = Number(n);
  if (!isFinite(num)) return '—';
  return `${num.toLocaleString()} ر.س`;
};

// ─── Drawer ──────────────────────────────────────────────
const ApprovalDrawer = ({ orderId, onClose, onChanged }) => {
  const [loading, setLoading] = useState(true);
  const [req, setReq] = useState(null);
  const [action, setAction] = useState(null); // 'approve' | 'reject' | 'request-new'
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const closeBtnRef = useRef(null);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminPaymentOrdersAPI.getDetail(orderId);
      const data = res.data?.data || res.data;
      setReq(data);
    } catch (e) {
      toast.error('فشل تحميل تفاصيل الطلب');
      onClose();
    } finally {
      setLoading(false);
    }
  }, [orderId, onClose]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  // ESC to close + focus close button
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape') {
        if (action) setAction(null);
        else onClose();
      }
    };
    window.addEventListener('keydown', handler);
    closeBtnRef.current?.focus();
    return () => window.removeEventListener('keydown', handler);
  }, [action, onClose]);

  const handleApprove = async () => {
    setSubmitting(true);
    try {
      await adminPaymentOrdersAPI.approve(orderId);
      toast.success('تم اعتماد التحويل وتفعيل الاشتراك');
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'فشل في المصادقة على التحويل');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!reason.trim()) {
      toast.error('يرجى كتابة سبب الرفض');
      return;
    }
    setSubmitting(true);
    try {
      await adminPaymentOrdersAPI.reject(orderId, reason.trim());
      toast.success('تم رفض الطلب');
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'فشل في رفض الطلب');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestNewReceipt = async () => {
    setSubmitting(true);
    try {
      await adminPaymentOrdersAPI.requestNewReceipt(orderId);
      toast.success('تم إرسال طلب إيصال جديد للمختبر');
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'فشل في إرسال الطلب');
    } finally {
      setSubmitting(false);
    }
  };

  const tenant = req?.tenant || req?.tenantId || {};
  const plan = req?.plan || req?.planId || {};
  const planLabel = plan?.nameAr || plan?.name || req?.planName || '—';
  const owner = tenant?.owner || tenant?.ownerName || req?.ownerName || '—';
  const email = tenant?.email || req?.email || '—';
  const phone = tenant?.phone || req?.phone || '—';
  const vat = tenant?.vatNumber || tenant?.vat || req?.vatNumber || '—';
  const labName = tenant?.name || req?.tenantName || '—';
  const branches = req?.branches ?? req?.branchCount ?? tenant?.branchCount ?? '—';
  const discount = req?.discountCode
    ? `${req.discountCode}${req.discountLabel ? ` (${req.discountLabel})` : ''}`
    : '—';
  const amount = req?.finalAmount ?? req?.amount ?? 0;
  const last4 = req?.last4 || req?.transferLast4 || '—';
  const transferDate = req?.transferDate || req?.sentAt || req?.createdAt;
  const receiptUrl = req?.receiptUrl || req?.receipt?.url;
  const receiptName = req?.receiptName || req?.receipt?.filename || 'receipt';
  const receiptIsImage = /\.(png|jpe?g|webp|gif)$/i.test(receiptName) || /image\//i.test(req?.receiptMimeType || '');
  const history = Array.isArray(req?.history) ? req.history : req?.auditLog || [];
  const reviewer = req?.reviewedBy?.email || req?.reviewerEmail;
  const reviewedAt = req?.reviewedAt || req?.updatedAt;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex"
        style={{ background: 'rgba(24,20,16,0.5)' }}
        onClick={onClose}
        dir="rtl"
        role="dialog"
        aria-modal="true"
        aria-label="تفاصيل طلب المصادقة"
      >
        <motion.aside
          initial={{ x: '-100%' }}
          animate={{ x: 0 }}
          exit={{ x: '-100%' }}
          transition={{ type: 'spring', damping: 28, stiffness: 280 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white h-full overflow-y-auto shadow-2xl"
          style={{ width: 560, maxWidth: '92vw', marginInlineEnd: 'auto' }}
        >
          {/* Header */}
          <div
            className="sticky top-0 bg-white z-10 flex items-center justify-between px-5 py-4"
            style={{ borderBottom: `1px solid ${BRAND.border}` }}
          >
            <div className="min-w-0">
              <div
                className="text-[11px] font-medium"
                style={{ color: BRAND.muted, letterSpacing: 0.5 }}
              >
                REQUEST {(req?.requestCode || req?._id || orderId || '').toString().toUpperCase()}
              </div>
              <div className="text-lg font-bold mt-0.5 truncate" style={{ color: BRAND.text }}>
                {loading ? '...' : labName}
              </div>
            </div>
            <button
              ref={closeBtnRef}
              onClick={onClose}
              aria-label="إغلاق"
              className="w-8 h-8 flex items-center justify-center rounded-lg border bg-white hover:bg-gray-50"
              style={{ borderColor: BRAND.border }}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="space-y-4 animate-pulse">
                <div className="h-6 w-24 bg-gray-200 rounded" />
                <div className="h-20 bg-gray-100 rounded-xl" />
                <div className="h-20 bg-gray-100 rounded-xl" />
                <div className="h-28 bg-gray-100 rounded-xl" />
              </div>
            ) : req ? (
              <>
                <div className="mb-2"><StatusBadge status={req.status} /></div>

                <SectionTitle>بيانات المختبر</SectionTitle>
                <InfoGrid data={[
                  ['صاحب الحساب', owner],
                  ['البريد', email],
                  ['الجوال', phone],
                  ['الرقم الضريبي', vat],
                ]} />

                <SectionTitle>الطلب</SectionTitle>
                <InfoGrid data={[
                  ['الباقة', planLabel],
                  ['عدد الفروع', String(branches)],
                  ['كود الخصم', discount],
                  ['المبلغ النهائي', formatAmount(amount)],
                ]} />

                <SectionTitle>إيصال التحويل</SectionTitle>
                <div
                  className="rounded-xl p-4 flex gap-3.5"
                  style={{ background: BRAND.surfaceAlt }}
                >
                  <div
                    className="flex-shrink-0 flex items-center justify-center flex-col gap-1.5 rounded-lg bg-white overflow-hidden"
                    style={{ width: 120, height: 150, border: `1px solid ${BRAND.border}` }}
                  >
                    {receiptUrl && receiptIsImage ? (
                      <img
                        src={receiptUrl}
                        alt="receipt"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <>
                        <FileText className="w-8 h-8" style={{ color: BRAND.muted }} />
                        <div className="text-[10px] truncate max-w-[100px]" style={{ color: BRAND.muted }}>
                          {receiptName}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="flex-1 text-[13px] flex flex-col gap-1 min-w-0">
                    <div>
                      <span style={{ color: BRAND.muted }}>تاريخ التحويل: </span>
                      <b>{formatDate(transferDate)}</b>
                    </div>
                    <div>
                      <span style={{ color: BRAND.muted }}>آخر 4 أرقام: </span>
                      <b className="font-mono">{last4}</b>
                    </div>
                    <a
                      href={receiptUrl || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => {
                        if (!receiptUrl) {
                          e.preventDefault();
                          toast.error('لا يوجد إيصال مرفوع');
                        }
                      }}
                      className="self-start mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors hover:bg-white"
                      style={{ borderColor: BRAND.border, color: BRAND.greenDark }}
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      عرض الإيصال
                    </a>
                  </div>
                </div>

                {req.status === 'pending' && (
                  <>
                    <SectionTitle>القرار</SectionTitle>
                    {action === 'reject' ? (
                      <div>
                        <label className="block text-[13px] font-medium mb-2" style={{ color: BRAND.text }}>
                          سبب الرفض <span style={{ color: BRAND.error }}>*</span>
                        </label>
                        <textarea
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                          placeholder="يرجى توضيح سبب الرفض للمستخدم"
                          autoFocus
                          className="w-full box-border p-3.5 text-sm rounded-xl outline-none focus:ring-2"
                          style={{
                            border: `1px solid ${BRAND.border}`,
                            minHeight: 100,
                            fontFamily: 'inherit',
                          }}
                        />
                        <div className="flex gap-2.5 mt-3">
                          <button
                            onClick={() => { setAction(null); setReason(''); }}
                            disabled={submitting}
                            className="flex-1 py-2.5 rounded-xl text-sm font-semibold border bg-white hover:bg-gray-50 disabled:opacity-50"
                            style={{ borderColor: BRAND.border, color: BRAND.text }}
                          >
                            إلغاء
                          </button>
                          <button
                            onClick={handleReject}
                            disabled={submitting || !reason.trim()}
                            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
                            style={{ background: BRAND.error }}
                          >
                            {submitting ? 'جارٍ الرفض...' : 'تأكيد الرفض'}
                          </button>
                        </div>
                      </div>
                    ) : action === 'approve' ? (
                      <div className="rounded-2xl p-5 text-center" style={{ background: BRAND.mint }}>
                        <div className="flex justify-center">
                          <CheckCircle2 className="w-8 h-8" style={{ color: BRAND.greenDark }} />
                        </div>
                        <div className="text-[15px] font-semibold mt-2" style={{ color: BRAND.greenDark }}>
                          هل أنت متأكد من المصادقة؟
                        </div>
                        <div className="text-[13px] mt-1" style={{ color: BRAND.greenDark, opacity: 0.85 }}>
                          سيتم تفعيل اشتراك المختبر وإرسال إيميل + SMS تأكيد.
                        </div>
                        <div className="flex gap-2.5 justify-center mt-4">
                          <button
                            onClick={() => setAction(null)}
                            disabled={submitting}
                            className="px-5 py-2 rounded-xl text-sm font-semibold border bg-white hover:bg-gray-50 disabled:opacity-50"
                            style={{ borderColor: BRAND.border, color: BRAND.text }}
                          >
                            إلغاء
                          </button>
                          <button
                            onClick={handleApprove}
                            disabled={submitting}
                            className="px-5 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
                            style={{ background: BRAND.green }}
                          >
                            {submitting ? 'جارٍ التفعيل...' : 'نعم، صادق'}
                          </button>
                        </div>
                      </div>
                    ) : action === 'request-new' ? (
                      <div
                        className="rounded-2xl p-5 text-center"
                        style={{ background: '#FFF9EC', border: `1px solid #F5E0A8` }}
                      >
                        <div className="flex justify-center">
                          <AlertCircle className="w-8 h-8" style={{ color: '#8A5A00' }} />
                        </div>
                        <div className="text-[15px] font-semibold mt-2" style={{ color: '#8A5A00' }}>
                          طلب إيصال جديد؟
                        </div>
                        <div className="text-[13px] mt-1" style={{ color: '#8A5A00', opacity: 0.85 }}>
                          سيتم إشعار المختبر لرفع إيصال جديد. يبقى الطلب في حالة "قيد المراجعة".
                        </div>
                        <div className="flex gap-2.5 justify-center mt-4">
                          <button
                            onClick={() => setAction(null)}
                            disabled={submitting}
                            className="px-5 py-2 rounded-xl text-sm font-semibold border bg-white hover:bg-gray-50 disabled:opacity-50"
                            style={{ borderColor: BRAND.border, color: BRAND.text }}
                          >
                            إلغاء
                          </button>
                          <button
                            onClick={handleRequestNewReceipt}
                            disabled={submitting}
                            className="px-5 py-2 rounded-xl text-sm font-semibold text-white disabled:opacity-50"
                            style={{ background: '#8A5A00' }}
                          >
                            {submitting ? 'جارٍ الإرسال...' : 'نعم، أرسل الطلب'}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-2.5">
                        <button
                          onClick={() => setAction('approve')}
                          className="py-2.5 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-1.5 hover:opacity-90"
                          style={{ background: BRAND.green }}
                        >
                          <Check className="w-4 h-4" />
                          مصادقة وتفعيل
                        </button>
                        <button
                          onClick={() => setAction('reject')}
                          className="py-2.5 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-1.5 hover:opacity-90"
                          style={{ background: BRAND.error }}
                        >
                          <XCircle className="w-4 h-4" />
                          رفض
                        </button>
                        <button
                          onClick={() => setAction('request-new')}
                          className="col-span-2 py-2.5 rounded-xl text-sm font-semibold border bg-white hover:bg-gray-50 flex items-center justify-center gap-1.5"
                          style={{ borderColor: BRAND.border, color: BRAND.text }}
                        >
                          <RefreshCw className="w-4 h-4" />
                          طلب إيصال جديد
                        </button>
                      </div>
                    )}
                  </>
                )}

                {req.status === 'rejected' && req.rejectReason && (
                  <>
                    <SectionTitle>سبب الرفض</SectionTitle>
                    <div
                      className="rounded-xl p-3.5 text-[13.5px]"
                      style={{
                        background: 'rgba(226,75,74,0.06)',
                        border: `1px solid rgba(226,75,74,0.2)`,
                        color: BRAND.error,
                      }}
                    >
                      {req.rejectReason}
                    </div>
                  </>
                )}

                <SectionTitle>سجل الإجراءات</SectionTitle>
                <div
                  className="rounded-xl p-3.5 text-[12.5px] space-y-1"
                  style={{ background: BRAND.surfaceAlt, color: BRAND.muted }}
                >
                  <div>• طلب منشأ في {formatDateTime(req.createdAt || req.sentAt)}</div>
                  {history.length > 0 ? (
                    history.map((h, i) => (
                      <div key={i}>
                        • {h.action || h.type} في {formatDateTime(h.at || h.createdAt)}
                        {h.by?.email ? ` بواسطة ${h.by.email}` : ''}
                      </div>
                    ))
                  ) : req.status !== 'pending' ? (
                    <div>
                      • {req.status === 'approved' ? 'مصادَق' : 'مرفوض'}
                      {reviewedAt ? ` في ${formatDateTime(reviewedAt)}` : ''}
                      {reviewer ? ` بواسطة ${reviewer}` : ''}
                    </div>
                  ) : null}
                </div>
              </>
            ) : null}
          </div>
        </motion.aside>
      </motion.div>
    </AnimatePresence>
  );
};

// ─── Main Page ───────────────────────────────────────────
const PaymentApprovals = () => {
  const [tab, setTab] = useState('pending');
  const [orders, setOrders] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0, all: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedId, setSelectedId] = useState(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (tab !== 'all') params.status = tab;
      if (search) params.search = search;
      const res = await adminPaymentOrdersAPI.list(params);
      const result = res.data?.data || res.data || {};
      const items = Array.isArray(result) ? result : (result.items || result.orders || []);
      setOrders(items);
      setTotalPages(result.totalPages || result.pages || 1);
      if (result.counts) {
        setCounts({
          pending: result.counts.pending ?? 0,
          approved: result.counts.approved ?? 0,
          rejected: result.counts.rejected ?? 0,
          all: result.counts.all ?? result.counts.total ?? 0,
        });
      }
    } catch (e) {
      toast.error('فشل في تحميل طلبات المصادقة');
    } finally {
      setLoading(false);
    }
  }, [tab, page, search]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);
  useEffect(() => { setPage(1); }, [tab, search]);

  return (
    <div dir="rtl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: BRAND.text }}>
            طلبات مصادقة التحويلات
          </h1>
          <p className="text-sm mt-1" style={{ color: BRAND.muted }}>
            راجع الإيصالات ووافق أو ارفض كل طلب.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div
        className="flex gap-1 mb-5 overflow-x-auto"
        style={{ borderBottom: `1px solid ${BRAND.border}` }}
        role="tablist"
      >
        {TABS.map((t) => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              role="tab"
              aria-selected={active}
              className="px-4 py-2.5 text-[13.5px] transition-colors flex items-center gap-2 whitespace-nowrap"
              style={{
                background: 'transparent',
                border: 'none',
                borderBottom: `2px solid ${active ? BRAND.green : 'transparent'}`,
                color: active ? BRAND.text : BRAND.muted,
                fontWeight: active ? 600 : 500,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {t.label}
              <span
                className="text-[11px] font-semibold rounded-full"
                style={{
                  padding: '1px 8px',
                  background: active ? BRAND.mint : BRAND.surfaceAlt,
                  color: active ? BRAND.greenDark : BRAND.muted,
                }}
              >
                {counts[t.key] ?? 0}
              </span>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="relative mb-4 max-w-md">
        <Search
          className="absolute w-4 h-4 top-1/2 -translate-y-1/2"
          style={{ insetInlineStart: 12, color: BRAND.muted }}
        />
        <input
          type="text"
          placeholder="بحث باسم المختبر، الرقم الضريبي، آخر 4 أرقام..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full py-2.5 bg-white rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          style={{
            border: `1px solid ${BRAND.border}`,
            paddingInlineStart: 36,
            paddingInlineEnd: 14,
          }}
        />
      </div>

      {/* Table */}
      <div
        className="bg-white rounded-2xl overflow-hidden"
        style={{ border: `1px solid ${BRAND.border}` }}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-[13.5px]">
            <thead>
              <tr style={{ background: BRAND.surfaceAlt, borderBottom: `1px solid ${BRAND.border}` }}>
                {['المختبر', 'الباقة', 'المبلغ', 'تاريخ الإرسال', 'آخر 4 أرقام', 'الحالة', ''].map((h, i) => (
                  <th
                    key={i}
                    className="text-start px-4 py-3 text-xs font-semibold"
                    style={{ color: BRAND.muted, letterSpacing: 0.3 }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="animate-pulse" style={{ borderBottom: `1px solid ${BRAND.surfaceAlt}` }}>
                    {[...Array(7)].map((_, j) => (
                      <td key={j} className="px-4 py-4">
                        <div className="h-4 bg-gray-200 rounded" style={{ width: j === 0 ? 140 : 80 }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : orders.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-14" style={{ color: BRAND.muted }}>
                    <Inbox className="w-10 h-10 mx-auto mb-2" style={{ color: '#D5D3CC' }} />
                    لا توجد طلبات في هذه الحالة
                  </td>
                </tr>
              ) : (
                orders.map((r) => {
                  const labName = r.tenant?.name || r.tenantId?.name || r.tenantName || '—';
                  const planLabel = r.plan?.nameAr || r.plan?.name || r.planId?.nameAr || r.planId?.name || r.planName || '—';
                  const amount = r.finalAmount ?? r.amount ?? 0;
                  const last4 = r.last4 || r.transferLast4 || '—';
                  const sentAt = r.createdAt || r.sentAt;
                  return (
                    <tr
                      key={r._id || r.id}
                      onClick={() => setSelectedId(r._id || r.id)}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setSelectedId(r._id || r.id);
                        }
                      }}
                      className="cursor-pointer transition-colors hover:bg-[#F7F6F1] focus:bg-[#F7F6F1] focus:outline-none"
                      style={{ borderBottom: `1px solid ${BRAND.surfaceAlt}` }}
                    >
                      <td className="px-4 py-3.5 font-semibold" style={{ color: BRAND.text }}>{labName}</td>
                      <td className="px-4 py-3.5" style={{ color: BRAND.muted }}>{planLabel}</td>
                      <td
                        className="px-4 py-3.5 font-semibold"
                        style={{ fontVariantNumeric: 'tabular-nums' }}
                      >
                        {formatAmount(amount)}
                      </td>
                      <td className="px-4 py-3.5" style={{ color: BRAND.muted, fontVariantNumeric: 'tabular-nums' }}>
                        {formatDateTime(sentAt)}
                      </td>
                      <td className="px-4 py-3.5 font-mono" style={{ color: BRAND.muted }}>
                        •••• {last4}
                      </td>
                      <td className="px-4 py-3.5"><StatusBadge status={r.status} /></td>
                      <td className="px-4 py-3.5 text-end">
                        <span
                          className="text-xs font-medium inline-flex items-center gap-1"
                          style={{ color: BRAND.greenDark }}
                        >
                          عرض
                          <ChevronLeft className="w-3.5 h-3.5" />
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{ borderTop: `1px solid ${BRAND.border}` }}
          >
            <p className="text-sm" style={{ color: BRAND.muted }}>
              صفحة {page} من {totalPages}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-40"
                aria-label="السابق"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-40"
                aria-label="التالي"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Drawer */}
      {selectedId && (
        <ApprovalDrawer
          orderId={selectedId}
          onClose={() => setSelectedId(null)}
          onChanged={fetchOrders}
        />
      )}
    </div>
  );
};

export default PaymentApprovals;
